import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { AuthUserContext } from '../Session';
import FirebaseContext from '../Firebase/context';
import { SERVER_URL } from '../../DB_Url';

const STORAGE_KEY = 'app/selectedProject';

const ProjectSelectionContext = createContext({
  projects: [],
  projectsMeta: {},
  selectedProject: '',
  setSelectedProject: () => {},
  loadingProjects: false,
  refreshProjects: () => {},
});

// Normalised key used to dedupe project names coming from the Flask API
// (projects that have uploaded documents) and from Firestore (every registered
// project, including those with no plans uploaded yet).
const normalizeKey = (name) => String(name || '').trim().toLowerCase();

const readStoredProject = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch (_error) {
    return '';
  }
};

export const ProjectSelectionProvider = ({ children }) => {
  const authUser = useContext(AuthUserContext);
  const firebase = useContext(FirebaseContext);
  const [projects, setProjects] = useState([]);
  const [projectsMeta, setProjectsMeta] = useState({});
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProject, setSelectedProjectState] = useState(() => readStoredProject());
  const [refreshKey, setRefreshKey] = useState(0);

  const refreshProjects = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const setSelectedProject = useCallback((projectName) => {
    const next = projectName || '';
    setSelectedProjectState(next);

    try {
      if (next) {
        localStorage.setItem(STORAGE_KEY, next);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (_error) {
      // Ignore localStorage errors.
    }
  }, []);

  useEffect(() => {
    if (!authUser?.roles) {
      setProjects([]);
      setProjectsMeta({});
      return;
    }

    const userOrgs = Object.keys(authUser.roles).filter((key) => authUser.roles[key] !== null);
    if (userOrgs.length === 0) {
      setProjects([]);
      setProjectsMeta({});
      return;
    }

    let mounted = true;
    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        // Source 1: Flask API — only projects that have uploaded documents.
        const apiProjects = await (async () => {
          try {
            const projectsUrl = new URL(`${SERVER_URL}/api/projects`);
            projectsUrl.searchParams.append('organisations', userOrgs.join(','));

            const response = await fetch(projectsUrl);
            if (!response.ok) throw new Error('Failed to fetch projects');

            const list = await response.json();
            return Array.isArray(list) ? list.filter(Boolean) : [];
          } catch (error) {
            console.error('Failed to fetch projects for shared selector:', error);
            return [];
          }
        })();

        // Source 2: Firestore — every registered project per org, including
        // projects created via the UI that have no uploaded plans yet.
        // Keeps the org each project came from so hosts (e.g. the Replan
        // upload modal) can pass the right organisation without extra reads.
        const firestoreProjects = await (async () => {
          if (!firebase?.doGetOrganizationProjects) return [];
          const perOrg = await Promise.all(
            userOrgs.map(async (org) => {
              try {
                const orgProjects = await firebase.doGetOrganizationProjects(org);
                return Array.isArray(orgProjects)
                  ? orgProjects
                      .map((p) => ({ name: p.name || p.id || '', organisation: org }))
                      .filter((entry) => entry.name)
                  : [];
              } catch (error) {
                // Org unreadable (e.g. rules not deployed yet): fall back to
                // the API list only for that org instead of failing everything.
                console.error(`Failed to fetch Firestore projects for org "${org}":`, error);
                return [];
              }
            }),
          );
          return perOrg.flat();
        })();

        if (!mounted) return;

        // Merge: API names first (they have documents), then any Firestore-only
        // projects, deduplicated on the normalised name.
        const apiKeys = new Set(apiProjects.map(normalizeKey));
        const merged = [...apiProjects];
        const seen = new Set(apiKeys);
        const firestoreByKey = new Map();
        firestoreProjects.forEach(({ name, organisation }) => {
          const key = normalizeKey(name);
          if (!key) return;
          if (!firestoreByKey.has(key)) firestoreByKey.set(key, { name, organisation });
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(name);
          }
        });
        merged.sort((a, b) => a.localeCompare(b));

        // Per-project metadata so the UI can later distinguish projects that
        // have uploaded plans from those that are registered only in Firestore.
        // `organisation` is the Firestore org the project belongs to (only set
        // for Firestore-sourced entries).
        const meta = {};
        merged.forEach((name) => {
          const key = normalizeKey(name);
          const hasDocuments = apiKeys.has(key);
          const fsEntry = firestoreByKey.get(key);
          meta[name] = {
            hasDocuments,
            source: hasDocuments ? 'api' : 'firestore',
            ...(fsEntry?.organisation ? { organisation: fsEntry.organisation } : {}),
          };
        });

        setProjects(merged);
        setProjectsMeta(meta);
        if (merged.length > 0 && !merged.includes(selectedProject)) {
          setSelectedProject(merged[0]);
        }
      } catch (error) {
        console.error('Failed to load projects for shared selector:', error);
        if (mounted) {
          setProjects([]);
          setProjectsMeta({});
        }
      } finally {
        if (mounted) setLoadingProjects(false);
      }
    };

    fetchProjects();

    return () => {
      mounted = false;
    };
  }, [authUser, selectedProject, setSelectedProject, refreshKey, firebase]);

  const value = useMemo(() => ({
    projects,
    projectsMeta,
    selectedProject,
    setSelectedProject,
    loadingProjects,
    refreshProjects,
  }), [projects, projectsMeta, selectedProject, setSelectedProject, loadingProjects, refreshProjects]);

  return (
    <ProjectSelectionContext.Provider value={value}>
      {children}
    </ProjectSelectionContext.Provider>
  );
};

export const useProjectSelection = () => useContext(ProjectSelectionContext);
export { ProjectSelectionContext };
